import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {AnimationStylesPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/animation-styles-panel/animation-styles-panel';
import type {AnimationSpeed, SlideType} from '@PosterWhiteboard/classes/item-animation.class';
import {PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromItemIntroAnimation} from '@Hooks/poster-editor/useGetKeyFromItemIntroAnimation';
import {useGetKeyFromItemOutroAnimation} from '@Hooks/poster-editor/useGetKeyFromItemOutroAnimation';
import {useAppSelector} from '@/hooks';
import {getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import {AnimationTabType, PreviewType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AnimationType} from '@PosterWhiteboard/items/item/item.types';

export function ItemAnimationPanel(): ReactElement<any> | null {
  const [animationTab, setAnimationTab] = useState(AnimationTabType.ENTRANCE);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const introType = useGetKeyFromItemIntroAnimation('type');
  const introSpeed = useGetKeyFromItemIntroAnimation('speed');
  const introDirection = useGetKeyFromItemIntroAnimation('direction');
  const outroType = useGetKeyFromItemOutroAnimation('type');
  const outroSpeed = useGetKeyFromItemOutroAnimation('speed');
  const outroDirection = useGetKeyFromItemOutroAnimation('direction');

  if (!activeItem) {
    throw new Error('Unable to show animation options since the activeItem is null');
  }

  if (!introType || !introSpeed || !introDirection || !outroType || !outroSpeed || !outroDirection || currentPageDuration === undefined) {
    return null;
  }

  const getSelectedAnimation = (): AnimationType => {
    if (animationTab === AnimationTabType.BOTH) {
      return introType === outroType ? introType : AnimationType.NONE;
    }
    return animationTab === AnimationTabType.EXIT ? outroType : introType;
  };

  const activeSpeed = animationTab === AnimationTabType.EXIT ? outroSpeed : introSpeed;
  const activeDirection = animationTab === AnimationTabType.EXIT ? outroDirection : introDirection;

  const onAnimationTypeModified = (animationType: AnimationType): void => {
    const animationData = {
      type: animationType,
      direction: animationType === AnimationType.ZOOM ? PositionType.LEFT : PositionType.RIGHT,
    };
    const updateOptions = {checkForDurationUpdate: true, replayPosterOnUpdateDone: true};

    if (animationTab === AnimationTabType.ENTRANCE) {
      trackPosterBuilderGA4Events(GA4EventName.ANIMATION_ITEM_ENTRANCE, {[GA4EventParamName.ANIMATION_TYPE]: animationType, [GA4EventParamName.ITEM_TYPE]: activeItem.gitype});
      void activeItem.updateFromObject(
        {
          introAnimation: animationData,
          onPageStartTime: animationType === AnimationType.NONE ? 0 : activeItem.onPageStartTime,
        },
        updateOptions
      );
    } else if (animationTab === AnimationTabType.EXIT) {
      trackPosterBuilderGA4Events(GA4EventName.ANIMATION_ITEM_EXIT, {[GA4EventParamName.ANIMATION_TYPE]: animationType, [GA4EventParamName.ITEM_TYPE]: activeItem.gitype});
      void activeItem.updateFromObject(
        {
          outroAnimation: animationData,
          onPageEndTime: animationType === AnimationType.NONE ? currentPageDuration : activeItem.onPageEndTime,
        },
        updateOptions
      );
    } else {
      trackPosterBuilderGA4Events(GA4EventName.ANIMATION_ITEM_ENTRANCE, {[GA4EventParamName.ANIMATION_TYPE]: animationType, [GA4EventParamName.ITEM_TYPE]: activeItem.gitype});
      trackPosterBuilderGA4Events(GA4EventName.ANIMATION_ITEM_EXIT, {[GA4EventParamName.ANIMATION_TYPE]: animationType, [GA4EventParamName.ITEM_TYPE]: activeItem.gitype});
      void activeItem.updateFromObject(
        {
          introAnimation: animationData,
          outroAnimation: animationData,
          onPageStartTime: animationType === AnimationType.NONE ? 0 : activeItem.onPageStartTime,
          onPageEndTime: animationType === AnimationType.NONE ? currentPageDuration : activeItem.onPageEndTime,
        },
        updateOptions
      );
    }
  };

  const onAnimationSpeedModified = (undoable: boolean, replayPosterOnChange: boolean, speed: AnimationSpeed): void => {
    const updateOptions = {checkForDurationUpdate: true, replayPosterOnUpdateDone: replayPosterOnChange, undoable};
    const suppressedOptions = {checkForDurationUpdate: false, undoable: false, fireModifiedEvent: false, replayPosterOnUpdateDone: false};

    if (animationTab === AnimationTabType.ENTRANCE || animationTab === AnimationTabType.BOTH) {
      void activeItem.updateFromObject({introAnimation: {speed}}, animationTab === AnimationTabType.BOTH ? suppressedOptions : updateOptions);
    }
    if (animationTab === AnimationTabType.EXIT || animationTab === AnimationTabType.BOTH) {
      void activeItem.updateFromObject({outroAnimation: {speed}}, animationTab === AnimationTabType.BOTH ? {...updateOptions, replayPosterOnUpdateDone: false} : updateOptions);
    }
    if (animationTab === AnimationTabType.BOTH && replayPosterOnChange) {
      void window.posterEditor?.whiteboard?.replayPage(window.posterEditor?.whiteboard?.getCurrentPage()?.hashedID ?? '');
    }
  };

  const onAnimationDirectionModified = (direction: SlideType | PositionType): void => {
    const updateOptions = {checkForDurationUpdate: true, replayPosterOnUpdateDone: true};
    const suppressedOptions = {checkForDurationUpdate: false, undoable: false, fireModifiedEvent: false, replayPosterOnUpdateDone: false};

    if (animationTab === AnimationTabType.ENTRANCE || animationTab === AnimationTabType.BOTH) {
      void activeItem.updateFromObject({introAnimation: {direction}}, animationTab === AnimationTabType.BOTH ? suppressedOptions : updateOptions);
    }
    if (animationTab === AnimationTabType.EXIT || animationTab === AnimationTabType.BOTH) {
      void activeItem.updateFromObject({outroAnimation: {direction}}, animationTab === AnimationTabType.BOTH ? {...updateOptions, replayPosterOnUpdateDone: false} : updateOptions);
    }
    if (animationTab === AnimationTabType.BOTH) {
      void window.posterEditor?.whiteboard?.replayPage(window.posterEditor?.whiteboard?.getCurrentPage()?.hashedID ?? '');
    }
  };

  const getAnimations = (): AnimationType[] => {
    const animationStyles = [AnimationType.NONE];
    if (activeItem.isText() || activeItem.isSlideshow()) {
      animationStyles.push(AnimationType.BLOCK);
    }
    if (animationTab === AnimationTabType.ENTRANCE) {
      animationStyles.push(AnimationType.BOUNCE);
    }
    animationStyles.push(
      AnimationType.FADE,
      AnimationType.JELLO,
      AnimationType.PAN,
      AnimationType.POP,
      AnimationType.ROTATE,
      AnimationType.SCALE,
      AnimationType.SLIDE,
      AnimationType.WIPE,
      AnimationType.PEEK,
      AnimationType.ZOOM
    );
    return animationStyles;
  };

  const getPreviewType = (): PreviewType => {
    if (activeItem?.isText() || activeItem?.isMenu() || activeItem?.isFancyText() || activeItem?.isSchedule() || activeItem?.isTableItem() || activeItem?.isTab()) {
      return PreviewType.TEXT;
    }
    if (activeItem?.isSlideshow()) {
      const slide = animationTab === AnimationTabType.EXIT ? activeItem.slides.getLastSlide() : activeItem.slides.getFirstSlide();
      if (slide.isTextSlide()) {
        return PreviewType.TEXT;
      }
    }
    if (activeItem?.isSticker() || activeItem.isRectangle() || activeItem.isLine() || activeItem.isVector() || activeItem.isQRItem()) {
      return PreviewType.ELEMENT;
    }
    return PreviewType.MEDIA;
  };

  return (
    <AnimationStylesPanel
      selectedAnimation={getSelectedAnimation()}
      onAnimationTypeModified={onAnimationTypeModified}
      onAnimationSpeedModified={onAnimationSpeedModified}
      onAnimationDirectionModified={onAnimationDirectionModified}
      speed={activeSpeed}
      direction={activeDirection}
      animations={getAnimations()}
      showInGrid={true}
      previewType={getPreviewType()}
      animationTab={animationTab}
      onAnimationTabChange={setAnimationTab}
    />
  );
}
